import type { DependencyType } from './constants'

/** A predecessor link expressed against this schedule's own row numbers. */
export interface LocalLink { id?: string; predecessorId: string; type: DependencyType; lagDays: number }

export type ParseResult = { ok: true; links: LocalLink[] } | { ok: false; error: 'syntax' | 'unknown_row' | 'self' | 'duplicate'; token: string }

const TOKEN = /^(\d+)(FS|SS|FF|SF)?(?:([+-])(\d+)D?)?$/

/** Spec §9.4 grammar: `rowNumber [type] [lag]`, tokens separated by `,` or `;`, case-insensitive, whitespace ignored. */
export const parsePredecessors = (text: string, tree: { idByRowNumber: Map<number, string> }, selfId: string): ParseResult => {
  const links: LocalLink[] = []
  const seen = new Set<string>()
  for (const raw of text.split(/[,;]/)) {
    const token = raw.replace(/\s+/g, '').toUpperCase()
    if (!token) continue
    const m = TOKEN.exec(token)
    if (!m) return { ok: false, error: 'syntax', token: raw.trim() }
    const id = tree.idByRowNumber.get(Number(m[1]))
    if (!id) return { ok: false, error: 'unknown_row', token: raw.trim() }
    if (id === selfId) return { ok: false, error: 'self', token: raw.trim() }
    if (seen.has(id)) return { ok: false, error: 'duplicate', token: raw.trim() }
    seen.add(id)
    links.push({ predecessorId: id, type: (m[2] as DependencyType) ?? 'FS', lagDays: m[3] ? (m[3] === '-' ? -1 : 1) * Number(m[4]) : 0 })
  }
  return { ok: true, links }
}

/** Local predecessors only (both endpoints in this schedule); diffed by `predecessorId`. */
export const diffPredecessors = (current: LocalLink[], next: LocalLink[]) => {
  const curBy = new Map(current.map((l) => [l.predecessorId, l]))
  const nextBy = new Map(next.map((l) => [l.predecessorId, l]))
  return {
    create: next.filter((l) => !curBy.has(l.predecessorId)),
    update: next
      .filter((l) => { const c = curBy.get(l.predecessorId); return !!c && (c.type !== l.type || c.lagDays !== l.lagDays) })
      .map((l) => ({ ...l, id: curBy.get(l.predecessorId)!.id! })),
    remove: current.filter((l) => !nextBy.has(l.predecessorId)).map((l) => l.id!),
  }
}

/** Ascending by the predecessor's row number; type omitted when FS (the default), lag omitted when zero. */
export const formatPredecessors = (links: LocalLink[], tree: { rowNumberById: Map<string, number> }): string =>
  links
    .map((l) => ({ l, n: tree.rowNumberById.get(l.predecessorId) }))
    .filter((x): x is { l: LocalLink; n: number } => x.n != null)
    .sort((a, b) => a.n - b.n)
    .map(({ l, n }) => `${n}${l.type === 'FS' ? '' : l.type}${l.lagDays ? (l.lagDays > 0 ? `+${l.lagDays}d` : `${l.lagDays}d`) : ''}`)
    .join(', ')
